import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { _getCountries } from '@/api/lookup/queries';
import { LocationList } from '@/api/lookup/types';
import type { SocialPlatform } from '@/api/profile/queries';
import BriefcaseIcon from '@/assets/svg/ui-elements/briefcase.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import TrashIcon from '@/assets/svg/ui-elements/trash-2.svg';
import UserIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { ContactAddressFields } from '@/features/contact-address';
import { PLATFORMS } from '@/pages/settings/modules/my-profile-redesign/public-tab/social-platforms';
import { isLooselyValidPhone } from '@/pages/settings/modules/my-profile-redesign/public-tab/validators';
import {
  Alert,
  Button,
  Drawer,
  IconButton,
  Input,
  InputPhone,
  Select,
  Tabs,
} from '@/shared/design-system';
import { isValidEmail } from '@/utils/validate-email';

import { AvatarUpload } from '../../components/avatar-upload';
import type { SocialProfileKey } from '../../components/social-profiles-block';
import type { ContactData, ContactType, SocialLink } from '../../types';
import { ContactAutocomplete } from '../review-card-drawer/contact-autocomplete';
import { useOrganizationSuggestions } from '../review-card-drawer/use-suggestions';
import {
  AddSectionRow,
  FieldRows,
  FieldSection,
  FormSections,
  SocialRow,
  TypeTabs,
  ValueRow,
} from './styled';

type OptionalSection = 'company' | 'address' | 'website' | 'social' | 'jobTitle';

const ADD_SECTION_BUTTONS: { label: string; section: OptionalSection }[] = [
  { label: 'Add organization', section: 'company' },
  { label: 'Add address', section: 'address' },
  { label: 'Add website', section: 'website' },
  { label: 'Add social media', section: 'social' },
  { label: 'Add job title', section: 'jobTitle' },
];

const INDIVIDUAL_ONLY_SECTIONS: OptionalSection[] = ['company', 'jobTitle'];

const SOCIAL_OPTIONS = (Object.keys(PLATFORMS) as SocialPlatform[])
  .filter((platform): platform is SocialProfileKey => platform !== 'Website')
  .map((platform) => ({ value: platform, label: PLATFORMS[platform].label }));

const DEFAULT_SOCIAL_PLATFORM = SOCIAL_OPTIONS[0].value;

const EMPTY_ADDRESS = { country: '', city: '', street: '', street2: '', state: '', postalCode: '' };

const initialSections: Record<OptionalSection, boolean> = {
  company: false,
  address: false,
  website: false,
  social: false,
  jobTitle: false,
};

function deriveInitialSections(defaults: Partial<ContactData>): Record<OptionalSection, boolean> {
  return {
    company: Boolean(defaults.organizationId || defaults.organizationName),
    address: Boolean(defaults.addresses?.length),
    website: Boolean(defaults.websites?.length),
    social: Boolean(defaults.socialLinks?.length),
    jobTitle: Boolean(defaults.jobTitle),
  };
}

const updateAt = <T,>(list: T[], index: number, value: T): T[] =>
  list.map((item, i) => (i === index ? value : item));
const removeAt = <T,>(list: T[], index: number): T[] => list.filter((_, i) => i !== index);

interface ContactFormStepProps {
  defaults: Partial<ContactData>;
  /**
   * Activates org sub-form mode: locks type to organization, hides the type tabs,
   * changes title/submit label. onSubmit is expected to call createContact directly.
   */
  isOrgSubForm?: boolean;
  submitLabel: string;
  /** Set when the contact cannot be saved whatever is typed; shown above the form. */
  submitBlockedReason?: string;
  onSubmit: (_data: Partial<ContactData>) => void | Promise<void>;
  /** Called when the user clicks "Create new" in the org autocomplete. Omit to hide the action. */
  onCreateOrg?: (_name: string, _formSnapshot: Partial<ContactData>) => void;
  onBack: () => void;
  onClose: () => void;
}

export const ContactFormStep = ({
  defaults,
  isOrgSubForm = false,
  submitLabel,
  submitBlockedReason,
  onSubmit,
  onCreateOrg,
  onBack,
  onClose,
}: ContactFormStepProps) => {
  const [localData, setLocalData] = useState<Partial<ContactData>>(defaults);
  const [contactType, setContactType] = useState<ContactType>(
    isOrgSubForm ? 'organization' : (defaults.contactType ?? 'individual'),
  );
  const [sections, setSections] = useState(() => deriveInitialSections(defaults));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialContactType = useRef(contactType);

  const { data: countries = [], isLoading: isCountriesLoading } = useQuery(
    _getCountries({ list: LocationList.ADDRESS, includeRegions: true }),
  );
  const countryOptions = useMemo(
    () => countries.map((country) => ({ label: country.name, value: country.id })),
    [countries],
  );
  const regionOptionsFor = (countryId: string | null | undefined) =>
    countries
      .find((country) => country.id === countryId)
      ?.regions.map((region) => ({ label: region.name, value: region.id })) ?? [];

  const { suggestions: organizationSuggestions, isLoading: isSearchingOrganizations } =
    useOrganizationSuggestions(localData.organizationName);

  // Skip reset on initial render — compare against the value contactType started with.
  // Using initialContactType ref (not isFirstRender) so StrictMode double-invocation is harmless.
  useEffect(() => {
    if (contactType === initialContactType.current) return;
    setLocalData({ contactType });
    setSections(initialSections);
  }, [contactType]);

  const emails = localData.emails?.length ? localData.emails : [''];
  const phones = localData.phones?.length ? localData.phones : [''];
  const websites = localData.websites?.length ? localData.websites : [''];
  const addresses = localData.addresses?.length ? localData.addresses : [EMPTY_ADDRESS];
  const socialLinks = localData.socialLinks ?? [];

  const patch = (next: Partial<ContactData>) => setLocalData((prev) => ({ ...prev, ...next }));

  const isOrganization = contactType === 'organization';

  const showSection = (section: OptionalSection) =>
    setSections((prev) => ({ ...prev, [section]: true }));

  const hideSection = (section: OptionalSection, cleared: Partial<ContactData>) => {
    setSections((prev) => ({ ...prev, [section]: false }));
    patch(cleared);
  };

  const hiddenSections = ADD_SECTION_BUTTONS.filter(
    ({ section }) =>
      !sections[section] && !(isOrganization && INDIVIDUAL_ONLY_SECTIONS.includes(section)),
  );

  const emailErrors = emails.map((email) => Boolean(email) && !isValidEmail(email.trim()));
  const phoneErrors = phones.map((phone) => Boolean(phone) && !isLooselyValidPhone(phone));

  const isValid =
    !submitBlockedReason &&
    Boolean(localData.name?.trim()) &&
    (!localData.organizationName || Boolean(localData.organizationId)) &&
    !emailErrors.some(Boolean) &&
    !phoneErrors.some(Boolean) &&
    (!sections.address || addresses.every((address) => Boolean(address.country)));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...localData,
        contactType,
        emails: emails.map((email) => email.trim()).filter(Boolean),
        phones: phones.map((phone) => phone.trim()).filter(Boolean),
        websites: websites.map((website) => website.trim()).filter(Boolean),
        socialLinks: socialLinks.filter((link) => link.value.trim()),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateOrg = useCallback(
    (name: string) => {
      onCreateOrg?.(name, { ...localData, contactType });
    },
    [localData, contactType, onCreateOrg],
  );

  const deleteButton = (label: string, onClick: () => void) => (
    <IconButton aria-label={label} color="destructive" variant="ghost" size="sm" onClick={onClick}>
      <TrashIcon width={16} height={16} />
    </IconButton>
  );

  const addButton = (label: string, onClick: () => void) => (
    <Button variant="subtle" color="primary" size="sm" onClick={onClick}>
      <PlusIcon width={16} height={16} />
      {label}
    </Button>
  );

  return (
    <Drawer
      open
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={isOrganization ? 'Add organization' : 'Add contact'}
      description={
        isOrganization
          ? 'Create an organization by entering their details below'
          : 'Create a contact by entering their details below'
      }
      maxWidth={504}
      secondaryAction={{ children: 'Back', color: 'tertiary', onClick: onBack }}
      primaryAction={{
        children: submitLabel,
        onClick: handleSubmit,
        disabled: !isValid || isSubmitting,
      }}
    >
      <FormSections>
        {submitBlockedReason && (
          <Alert variant="info" size="md" description={submitBlockedReason} />
        )}
        {!isOrgSubForm && (
          <TypeTabs>
            <Tabs.Root
              value={contactType}
              onValueChange={(value) => setContactType(value as ContactType)}
              variant="boxed"
              size="lg"
            >
              <Tabs.List>
                <Tabs.Tab value="individual" icon={<UserIcon />} label="Individual" />
                <Tabs.Tab value="organization" icon={<BriefcaseIcon />} label="Organization" />
              </Tabs.List>
            </Tabs.Root>
          </TypeTabs>
        )}

        {isOrganization && (
          <AvatarUpload onChange={(fileId) => patch({ iconId: fileId ?? undefined })} />
        )}

        <Input
          size="lg"
          label={isOrganization ? 'Organization name' : 'Full Name'}
          required
          placeholder={isOrganization ? 'Enter Organization Name' : 'Enter full name'}
          value={localData.name ?? ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => patch({ name: event.target.value })}
        />

        <FieldSection>
          <FieldRows>
            {emails.map((email, index) => (
              <ValueRow key={index}>
                <Input
                  size="lg"
                  label={index === 0 ? 'Email' : undefined}
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  state={emailErrors[index] ? 'destructive' : 'default'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    patch({ emails: updateAt(emails, index, event.target.value) })
                  }
                />
                {index > 0 &&
                  deleteButton(`Remove email ${index + 1}`, () =>
                    patch({ emails: removeAt(emails, index) }),
                  )}
              </ValueRow>
            ))}
          </FieldRows>
          {addButton('Add email', () => patch({ emails: [...emails, ''] }))}
        </FieldSection>

        <FieldSection>
          <FieldRows>
            {phones.map((phone, index) => (
              <ValueRow key={index}>
                <InputPhone
                  label={index === 0 ? 'Phone' : undefined}
                  ariaLabel={`Phone ${index + 1}`}
                  placeholder="Enter phone"
                  value={phone}
                  state={phoneErrors[index] ? 'destructive' : undefined}
                  onChange={({ e164 }) => patch({ phones: updateAt(phones, index, e164) })}
                />
                {index > 0 &&
                  deleteButton(`Remove phone ${index + 1}`, () =>
                    patch({ phones: removeAt(phones, index) }),
                  )}
              </ValueRow>
            ))}
          </FieldRows>
          {addButton('Add phone', () => patch({ phones: [...phones, ''] }))}
        </FieldSection>

        {!isOrganization && sections.jobTitle && (
          <ValueRow>
            <Input
              size="lg"
              label="Job Title"
              placeholder="Enter job title"
              value={localData.jobTitle ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                patch({ jobTitle: event.target.value })
              }
            />
            {deleteButton('Remove job title', () =>
              hideSection('jobTitle', { jobTitle: undefined }),
            )}
          </ValueRow>
        )}

        {!isOrganization && sections.company && (
          <ValueRow>
            <ContactAutocomplete
              label="Company / Organization"
              placeholder="Enter company"
              value={localData.organizationName ?? ''}
              suggestions={organizationSuggestions}
              isLoading={isSearchingOrganizations}
              createNewLabel={onCreateOrg ? 'Create organization' : undefined}
              onChange={(value) => patch({ organizationName: value, organizationId: undefined })}
              onSelect={(suggestion) =>
                patch({ organizationName: suggestion.label, organizationId: suggestion.id })
              }
              onCreateNew={onCreateOrg ? handleCreateOrg : undefined}
            />
            {deleteButton('Remove company', () =>
              hideSection('company', { organizationName: undefined, organizationId: undefined }),
            )}
          </ValueRow>
        )}

        {sections.address && (
          <FieldSection>
            {addresses.map((address, index) => (
              <Fragment key={index}>
                <ValueRow>
                  <Select
                    size="lg"
                    label={index === 0 ? 'Address' : undefined}
                    required={index === 0}
                    options={countryOptions}
                    value={address.country || null}
                    placeholder="Enter country"
                    ariaLabel={`Country ${index + 1}`}
                    disabled={isCountriesLoading}
                    showSearch
                    onChange={(value) =>
                      patch({
                        addresses: updateAt(addresses, index, {
                          ...address,
                          country: value,
                          state: '',
                        }),
                      })
                    }
                  />
                  {deleteButton(`Remove address ${index + 1}`, () =>
                    addresses.length === 1
                      ? hideSection('address', { addresses: [] })
                      : patch({ addresses: removeAt(addresses, index) }),
                  )}
                </ValueRow>

                <ContactAddressFields
                  address={address}
                  index={index}
                  regionOptions={regionOptionsFor(address.country)}
                  isCountriesLoading={isCountriesLoading}
                  onChange={(next) => patch({ addresses: updateAt(addresses, index, next) })}
                />
              </Fragment>
            ))}
            {addButton('Add address', () =>
              patch({ addresses: [...addresses, { ...EMPTY_ADDRESS }] }),
            )}
          </FieldSection>
        )}

        {sections.website && (
          <FieldSection>
            <FieldRows>
              {websites.map((website, index) => (
                <ValueRow key={index}>
                  <Input
                    size="lg"
                    label={index === 0 ? 'Website' : undefined}
                    placeholder="Enter website URL"
                    value={website}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      patch({ websites: updateAt(websites, index, event.target.value) })
                    }
                  />
                  {deleteButton(`Remove website ${index + 1}`, () =>
                    websites.length === 1
                      ? hideSection('website', { websites: [] })
                      : patch({ websites: removeAt(websites, index) }),
                  )}
                </ValueRow>
              ))}
            </FieldRows>
            {addButton('Add website', () => patch({ websites: [...websites, ''] }))}
          </FieldSection>
        )}

        {sections.social && (
          <FieldSection>
            <FieldRows>
              {socialLinks.map((link, index) => (
                <SocialRow key={index}>
                  <Select
                    size="lg"
                    label={index === 0 ? 'Social media' : undefined}
                    options={SOCIAL_OPTIONS}
                    value={link.platform}
                    ariaLabel={`Social platform ${index + 1}`}
                    onChange={(value) =>
                      patch({
                        socialLinks: updateAt(socialLinks, index, {
                          ...link,
                          platform: value as SocialProfileKey,
                        }),
                      })
                    }
                  />
                  <Input
                    size="lg"
                    placeholder={PLATFORMS[link.platform]?.placeholder}
                    value={link.value}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      patch({
                        socialLinks: updateAt(socialLinks, index, {
                          ...link,
                          value: event.target.value,
                        }),
                      })
                    }
                  />
                  {deleteButton(`Remove social media ${index + 1}`, () =>
                    socialLinks.length === 1
                      ? hideSection('social', { socialLinks: [] })
                      : patch({ socialLinks: removeAt(socialLinks, index) }),
                  )}
                </SocialRow>
              ))}
            </FieldRows>
            {addButton('Add social profile', () =>
              patch({
                socialLinks: [
                  ...socialLinks,
                  { platform: DEFAULT_SOCIAL_PLATFORM, value: '' } as SocialLink,
                ],
              }),
            )}
          </FieldSection>
        )}

        {hiddenSections.length > 0 && (
          <AddSectionRow>
            {hiddenSections.map(({ label, section }) => (
              <Button
                key={section}
                variant="subtle"
                color="primary"
                size="sm"
                onClick={() => {
                  showSection(section);
                  if (section === 'social' && socialLinks.length === 0) {
                    patch({
                      socialLinks: [{ platform: DEFAULT_SOCIAL_PLATFORM, value: '' } as SocialLink],
                    });
                  }
                }}
              >
                <PlusIcon width={16} height={16} />
                {label}
              </Button>
            ))}
          </AddSectionRow>
        )}
      </FormSections>
    </Drawer>
  );
};
